import { useRef, useState } from "react";

// One write at a time from a dialog, so that pressing the button twice can't do
// the thing twice. A double-tap on a phone needs no intent at all, and on a
// create that is a whole second record: the payload says nothing about the first
// one, so the server has no way to tell the two apart. Updates are only slightly
// better — two PUTs and two audit rows for one edit.
//
// The ref is the real lock and `busy` only drives the UI: the two taps can both
// be handled before a re-render has disabled anything, so a `disabled` prop on
// its own is not a guard. Pass `busy` to the button as well, to say what's
// happening rather than silently swallowing the press.
//
//   const { busy, run } = useWriteLock();
//   const save = () => run(() => axios.post(API, payload).then(close));
//   <Button disabled={!canSave || busy} onClick={save}>Save</Button>
//
// `run` resolves when the write settles, and never rejects — the global axios
// interceptor surfaces the error (see authedAxios.js). `release` is for a dialog
// that can be closed while a write is in flight, so the lock doesn't outlive the
// dialog holding it.
export function useWriteLock() {
  const held = useRef(false);
  const [busy, setBusy] = useState(false);

  const release = () => { held.current = false; setBusy(false); };

  const run = (start) => {
    if (held.current) return Promise.resolve(null);  // a second tap
    held.current = true;
    setBusy(true);
    let started;
    try {
      started = Promise.resolve(start());
    } catch (err) {       // a synchronous throw would otherwise hold the lock
      release();
      throw err;
    }
    return started.catch(() => null).finally(release);
  };

  return { busy, run, release };
}
